import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { type OptionItem } from "@/components/formfields/SelectDropdown";
import {
  useResumeTemplates,
  useExportPreview,
  useExportPdf,
  useExportDocx,
  useExportBulkPdf,
  useExportBulkDocx,
} from "@/pages/templates/hooks/useTemplates";
import {
  useCandidateSkills,
  useCandidateProjects,
  useCandidateEducation,
  useCandidateCertifications,
  useCandidateAwards,
} from "@/pages/candidate-profile/hooks/useCandidateProfile";
import { TemplateSelector } from "./TemplateSelector";
import {
  ExcludeFieldsSelector,
  type SectionKey,
  type SectionItemOption,
} from "@/components/ExcludeFieldsSelector";
import { FileText, Loader2 } from "lucide-react";

interface ExportCandidatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIds: (string | number)[];
  selected_slug?: string;
  filename?: string
}

export function ExportCandidatesModal({
  isOpen,
  onClose,
  selectedIds,
  selected_slug,
  filename
}: ExportCandidatesModalProps) {
  const { data: apiTemplates, isLoading: isTemplatesLoading } =
    useResumeTemplates();
  const exportPdfMutation = useExportPdf();
  const exportDocxMutation = useExportDocx();
  const exportBulkPdfMutation = useExportBulkPdf();
  const exportBulkDocxMutation = useExportBulkDocx();

  const [template, setTemplate] = useState<string>(selected_slug || "");
  const [excludeFields, setExcludeFields] = useState<Record<string, boolean>>({
    avatar: false,
    job_role: false,
    experience_years: false,
    experience_months: false,
    summary: false,
    skills: false,
    educations: false,
    projects: false,
    certifications: false,
    awards: false,
  });

  const isSingleCandidate = selectedIds.length === 1;
  const candidateId = selectedIds.length > 0 ? selectedIds[0] : null;

  // Single-candidate profile section items queries (first 10 items)
  const paginationParams = useMemo(() => ({ page: 1, page_size: 12 }), []);

  const {
    data: skillsRes,
    fetchNextPage: fetchNextSkillsPage,
    hasNextPage: hasNextSkillsPage,
    isFetchingNextPage: isFetchingSkills,
  } = useCandidateSkills(
    isOpen && isSingleCandidate ? candidateId : undefined,
    paginationParams,
  );
  const {
    data: projectsRes,
    fetchNextPage: fetchNextProjectsPage,
    hasNextPage: hasNextProjectsPage,
    isFetchingNextPage: isFetchingProjects,
  } = useCandidateProjects(
    isOpen && isSingleCandidate ? candidateId : undefined,
    paginationParams,
  );
  const {
    data: educationRes,
    fetchNextPage: fetchNextEducationPage,
    hasNextPage: hasNextEducationPage,
    isFetchingNextPage: isFetchingEducation,
  } = useCandidateEducation(
    isOpen && isSingleCandidate ? candidateId : undefined,
    paginationParams,
  );
  const {
    data: certsRes,
    fetchNextPage: fetchNextCertsPage,
    hasNextPage: hasNextCertsPage,
    isFetchingNextPage: isFetchingCerts,
  } = useCandidateCertifications(
    isOpen && isSingleCandidate ? candidateId : undefined,
    paginationParams,
  );
  const {
    data: awardsRes,
    fetchNextPage: fetchNextAwardsPage,
    hasNextPage: hasNextAwardsPage,
    isFetchingNextPage: isFetchingAwards,
  } = useCandidateAwards(
    isOpen && isSingleCandidate ? candidateId : undefined,
    paginationParams,
  );

  const handleLoadMoreSection = (section: SectionKey) => {
    switch (section) {
      case "skills":
        if (hasNextSkillsPage && !isFetchingSkills) fetchNextSkillsPage();
        break;
      case "projects":
        if (hasNextProjectsPage && !isFetchingProjects) fetchNextProjectsPage();
        break;
      case "educations":
        if (hasNextEducationPage && !isFetchingEducation) fetchNextEducationPage();
        break;
      case "certifications":
        if (hasNextCertsPage && !isFetchingCerts) fetchNextCertsPage();
        break;
      case "awards":
        if (hasNextAwardsPage && !isFetchingAwards) fetchNextAwardsPage();
        break;
    }
  };

  // Excluded specific item IDs state (for single candidate mode)
  const [excludedItemIds, setExcludedItemIds] = useState<
    Record<SectionKey, (string | number)[]>
  >({
    projects: [],
    skills: [],
    educations: [],
    certifications: [],
    awards: [],
  });

  // Map API section lists to display options
  const sectionItems: Record<SectionKey, SectionItemOption[]> = useMemo(() => {
    if (!isSingleCandidate) {
      return {
        projects: [],
        skills: [],
        educations: [],
        certifications: [],
        awards: [],
      };
    }

    const getList = (res: any) => {
      if (!res) return [];
      if (res.pages) {
        return res.pages.flatMap((p: any) =>
          Array.isArray(p?.data) ? p.data : p?.data?.results || [],
        );
      }
      if (Array.isArray(res.data)) return res.data;
      return res.data?.results || [];
    };

    const projectsList = getList(projectsRes);
    const skillsList = getList(skillsRes);
    const educationList = getList(educationRes);
    const certsList = getList(certsRes);
    const awardsList = getList(awardsRes);

    return {
      projects: projectsList.map((p) => ({
        id: p.id,
        label: p.project_name || `Project #${p.id}`,
      })),
      skills: skillsList.map((s) => ({
        id: s.id,
        label: s.skill_name || `Skill #${s.id}`,
      })),
      educations: educationList.map((e) => ({
        id: e.id,
        label:
          e.degree?.degree_name ||
          e.course_name ||
          e.institution ||
          `Education #${e.id}`,
      })),
      certifications: certsList.map((c) => ({
        id: c.id,
        label: c.certification_name || `Certification #${c.id}`,
      })),
      awards: awardsList.map((a) => ({
        id: a.id,
        label: a.award_title || `Award #${a.id}`,
      })),
    };
  }, [
    isSingleCandidate,
    projectsRes,
    skillsRes,
    educationRes,
    certsRes,
    awardsRes,
  ]);

  const sectionTotalItems: Partial<Record<SectionKey, number>> = useMemo(() => {
    if (!isSingleCandidate) return {};

    const getTotalItems = (queryResult: any, listFallbackLength: number): number => {
      if (!queryResult) return listFallbackLength;
      if (queryResult.pages && queryResult.pages.length > 0) {
        const firstPage = queryResult.pages[0];
        const total =
          firstPage?.data?.totalItems ??
          firstPage?.data?.count ??
          firstPage?.data?.total ??
          firstPage?.totalItems ??
          firstPage?.count;
        if (typeof total === "number") return total;
      }
      const total =
        queryResult.data?.totalItems ??
        queryResult.data?.count ??
        queryResult.data?.total ??
        queryResult.totalItems ??
        queryResult.count;
      if (typeof total === "number") return total;
      return listFallbackLength;
    };

    return {
      projects: getTotalItems(projectsRes, sectionItems.projects.length),
      skills: getTotalItems(skillsRes, sectionItems.skills.length),
      educations: getTotalItems(educationRes, sectionItems.educations.length),
      certifications: getTotalItems(certsRes, sectionItems.certifications.length),
      awards: getTotalItems(awardsRes, sectionItems.awards.length),
    };
  }, [
    isSingleCandidate,
    projectsRes,
    skillsRes,
    educationRes,
    certsRes,
    awardsRes,
    sectionItems,
  ]);

  // Auto-set default template when API templates arrive
  useEffect(() => {
    if (selected_slug) {
      setTemplate(selected_slug);
    } else if (isOpen && apiTemplates && apiTemplates.length > 0 && !template) {
      setTemplate(apiTemplates[0].slug);
    }
  }, [isOpen, apiTemplates, template, selected_slug]);

  // Auto-select newly loaded sub-items if main section checkbox is selected
  useEffect(() => {
    if (!isSingleCandidate) return;

    setExcludedItemIds((prev) => {
      let updated = false;
      const nextState = { ...prev };

      (Object.keys(sectionItems) as SectionKey[]).forEach((section) => {
        if (excludeFields[section]) {
          const allItemIds = (sectionItems[section] || []).map((item) => item.id);
          const currentList = prev[section] || [];
          const hasMissing = allItemIds.some((id) => !currentList.includes(id));
          if (hasMissing) {
            nextState[section] = Array.from(new Set([...currentList, ...allItemIds]));
            updated = true;
          }
        }
      });

      return updated ? nextState : prev;
    });
  }, [isSingleCandidate, sectionItems, excludeFields]);

  // Handle specific item exclusion toggle for single candidate
  const handleItemExcludeChange = (
    section: SectionKey,
    itemId: string | number,
    checked: boolean,
  ) => {
    setExcludedItemIds((prev) => {
      const currentList = prev[section] || [];
      const newList = checked
        ? Array.from(new Set([...currentList, itemId]))
        : currentList.filter((id) => id !== itemId);

      // Check if all items in section are excluded to update master section state
      const totalItems = sectionItems[section] || [];
      const allSelected =
        totalItems.length > 0 &&
        totalItems.every((item) => newList.includes(item.id));

      setExcludeFields((prevFields) => ({
        ...prevFields,
        [section]: allSelected,
      }));

      return {
        ...prev,
        [section]: newList,
      };
    });
  };

  // Construct payload dynamically based on single vs multiple candidates
  const previewPayload = useMemo(() => {
    if (isSingleCandidate) {
      return {
        template,
        exclude: {
          avatar: Boolean(excludeFields.avatar),
          job_role: Boolean(excludeFields.job_role),
          experience_years: Boolean(excludeFields.experience_years),
          experience_months: Boolean(excludeFields.experience_months),
          summary: Boolean(excludeFields.summary),
          projects: excludeFields.projects ? false : excludedItemIds.projects,
          educations: excludeFields.educations ? false : excludedItemIds.educations,
          skills: excludeFields.skills ? false : excludedItemIds.skills,
          certifications: excludeFields.certifications ? false : excludedItemIds.certifications,
          awards: excludeFields.awards ? false : excludedItemIds.awards,
        },
      };
    }

    return {
      template,
      exclude: {
        avatar: Boolean(excludeFields.avatar),
        job_role: Boolean(excludeFields.job_role),
        experience_years: Boolean(excludeFields.experience_years),
        experience_months: Boolean(excludeFields.experience_months),
        summary: Boolean(excludeFields.summary),
        projects: excludeFields.projects ? true : [],
        educations: excludeFields.educations ? true : [],
        skills: excludeFields.skills ? true : [],
        certifications: excludeFields.certifications ? true : [],
        awards: excludeFields.awards ? true : [],
      },
    };
  }, [isSingleCandidate, template, excludeFields, excludedItemIds]);

  // useQuery hook with STALE_5_MIN and parameter-based key caching
  const {
    data: previewResponse,
    isFetching: isPreviewFetching,
    isLoading: isPreviewLoading,
    refetch: refetchPreview,
  } = useExportPreview(
    candidateId,
    previewPayload,
    isOpen && Boolean(template),
  );

  const previewHtml = previewResponse?.data?.html || "";

  const templateOptions: OptionItem[] = (apiTemplates || []).map((t) => ({
    label: t.display_name,
    value: t.slug,
  }));

  const selectedTemplateObj = (apiTemplates || []).find(
    (t) => t.slug === template,
  );

  const handleExcludeChange = (key: string, checked: boolean) => {
    setExcludeFields((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  const handleExportPdf = () => {
    if (!template) return;
    if (isSingleCandidate) {
      if (!candidateId) return;
      exportPdfMutation.mutate({ id: candidateId, payload: previewPayload, filename });
    } else {
      exportBulkPdfMutation.mutate({
        template,
        exclude: previewPayload.exclude,
        profile_ids: selectedIds,
        format: "pdf",
      });
    }
  };

  const handleExportDocx = () => {
    if (!template) return;
    if (isSingleCandidate) {
      if (!candidateId) return;
      exportDocxMutation.mutate({ id: candidateId, payload: previewPayload, filename });
    } else {
      exportBulkDocxMutation.mutate({
        template,
        exclude: previewPayload.exclude,
        profile_ids: selectedIds,
        format: "docx",
      });
    }
  };

  const isPreviewBusy = isPreviewLoading || isPreviewFetching;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] lg:max-w-[1200px] h-[90vh] max-h-[90vh] flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 overflow-hidden">
        <DialogHeader className="shrink-0 pb-3 border-b border-slate-200 dark:border-slate-800">
          <DialogTitle className="text-xl font-bold">
            Export Candidates to PDF
          </DialogTitle>
          <DialogDescription className="text-slate-500 dark:text-slate-400">
            Configure the export template and data exclusions.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 flex-1 overflow-y-auto min-h-0 p-4">
          {/* Configuration Column */}
          <div className="lg:col-span-2 space-y-6 overflow-y-auto px-2">
            <TemplateSelector
              value={template}
              onChange={setTemplate}
              options={templateOptions}
              isLoading={isTemplatesLoading}
              disabled={Boolean(selected_slug)}
            />

            <ExcludeFieldsSelector
              values={excludeFields}
              onChange={handleExcludeChange}
              isSingleCandidate={isSingleCandidate}
              sectionItems={sectionItems}
              sectionTotalItems={sectionTotalItems}
              excludedItemIds={excludedItemIds}
              onItemExcludeChange={handleItemExcludeChange}
              onLoadMore={handleLoadMoreSection}
              sectionHasMore={{
                skills: hasNextSkillsPage,
                projects: hasNextProjectsPage,
                educations: hasNextEducationPage,
                certifications: hasNextCertsPage,
                awards: hasNextAwardsPage,
              }}
              isFetchingSection={{
                skills: isFetchingSkills,
                projects: isFetchingProjects,
                educations: isFetchingEducation,
                certifications: isFetchingCerts,
                awards: isFetchingAwards,
              }}
            />
          </div>

          {/* Preview Column */}
          <div className="lg:col-span-3 bg-slate-100 dark:bg-slate-950 rounded-xl overflow-hidden relative border border-slate-200 dark:border-slate-800 flex justify-center items-center h-full min-h-0">
            <div className="absolute top-3 right-3 bg-slate-900/80 text-white text-[10px] font-bold px-2 py-1 rounded backdrop-blur-sm z-50 uppercase tracking-wider flex items-center gap-1.5">
              {isPreviewFetching && (
                <Loader2 className="w-3 h-3 animate-spin text-purple-400" />
              )}
              LIVE PREVIEW
            </div>
            <div className="w-full h-full overflow-y-auto overflow-x-hidden flex justify-center bg-slate-200/50 dark:bg-slate-900/50 p-4 pt-12">
              <div
                className="w-[21cm] min-h-[29.7cm] bg-white text-slate-900 shadow-2xl origin-top"
                style={{ transform: "scale(0.55)" }}
              >
                {isPreviewBusy && !previewHtml ? (
                  <div className="w-full h-full flex flex-col justify-center items-center text-slate-400 py-20">
                    <Loader2 className="w-10 h-10 animate-spin text-purple-600 mb-3" />
                    <span className="text-sm font-medium">
                      Loading preview...
                    </span>
                  </div>
                ) : previewHtml ? (
                  <div
                    className="w-full h-full text-lg"
                    dangerouslySetInnerHTML={{
                      __html: previewHtml,
                    }}
                  />
                ) : selectedTemplateObj?.html_template ? (
                  <div
                    className="w-full h-full text-lg"
                    dangerouslySetInnerHTML={{
                      __html: selectedTemplateObj.html_template,
                    }}
                  />
                ) : (
                  <div className="w-full min-h-[29.7cm] flex flex-col justify-center items-center text-slate-400 p-8 border-2 border-dashed border-slate-200 rounded-xl">
                    <FileText className="w-16 h-16 text-purple-400 mb-4" />
                    <h3 className="text-xl font-bold text-slate-800">
                      {selectedTemplateObj?.display_name || "Template Preview"}
                    </h3>
                    <p className="text-sm text-slate-500 mt-2 text-center">
                      {selectedTemplateObj?.description ||
                        "Select a template above to see live preview."}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer buttons */}
        <DialogFooter className="shrink-0 pt-3 border-t border-slate-200 dark:border-slate-800 flex flex-wrap sm:flex-nowrap items-center justify-end gap-2 w-full">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {/* <Button
            variant="outline"
            onClick={() => refetchPreview()}
            disabled={isPreviewBusy || !template || selectedIds.length === 0}
          >
            {isPreviewBusy && (
              <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
            )}
            Preview
          </Button> */}
          <Button
            onClick={handleExportDocx}
            disabled={
              (isSingleCandidate
                ? exportDocxMutation.isPending
                : exportBulkDocxMutation.isPending) || selectedIds.length === 0
            }
          >
            {(isSingleCandidate
              ? exportDocxMutation.isPending
              : exportBulkDocxMutation.isPending) ? (
              <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
            ) : (
              <FileText className="w-4 h-4 mr-1.5" />
            )}
            {selectedIds.length > 1
              ? `Export ${selectedIds.length} DOCS`
              : "Export DOC"}
          </Button>
          <Button
            onClick={handleExportPdf}
            disabled={
              (isSingleCandidate
                ? exportPdfMutation.isPending
                : exportBulkPdfMutation.isPending) || selectedIds.length === 0
            }
          >
            {(isSingleCandidate
              ? exportPdfMutation.isPending
              : exportBulkPdfMutation.isPending) ? (
              <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
            ) : (
              <FileText className="w-4 h-4 mr-1.5" />
            )}
            {selectedIds.length > 1
              ? `Export ${selectedIds.length} PDFs`
              : "Export PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
